import { brokersFieldLimits } from "@/Shared/Brokers/Helpers/brokersFieldLimits";
import type { Patch } from "@freightsimple/shared/types";
import AddressInput from "@/Shared/Locations/Inputs/LocationsAddressInput";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { useState } from "react";
import type { AddBrokerBrokerType } from "@/Shared/Types/brokerTypes";
import { brokersErrorMessagesForBroker } from "@/Shared/Brokers/Validators/brokersErrorMessagesForBroker";
import YellowExclamationAnimatedIcon from "@/Shared/Animations/YellowExclamationAnimatedIcon";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";

interface LocationsAddLocationDialogAddressPage {
  broker: AddBrokerBrokerType;
  setBroker: (_: AddBrokerBrokerType) => void;
  onSwitchToContactPage: () => void;
  onCancel: () => void;
}

function BrokersAddBrokerDialogAddressPage(
  props: LocationsAddLocationDialogAddressPage,
) {
  const { broker, setBroker, onSwitchToContactPage, onCancel } = props;
  const [forceValidation, setForceValidation] = useState(false);

  function updateAddress(addressUpdates: Patch<Address>) {
    const address = {
      ...broker.address,
      ...addressUpdates,
    };

    setBroker({
      ...broker,
      address,
    });
  }

  const errorMessages = brokersErrorMessagesForBroker(broker);

  function setBusinessName(businessName: string) {
    setBroker({
      ...broker,
      businessName,
    });
  }

  function setAccountNumber(accountNumber: string) {
    setBroker({
      ...broker,
      accountNumber,
    });
  }

  function isValid() {
    if (errorMessages.businessName !== undefined) {
      return false;
    }

    if (errorMessages.address.addressLine !== undefined) {
      return false;
    }
    if (errorMessages.address.city !== undefined) {
      return false;
    }
    if (errorMessages.address.stateOrProvinceCode !== undefined) {
      return false;
    }
    if (errorMessages.address.postalCode !== undefined) {
      return false;
    }
    if (errorMessages.address.country !== undefined) {
      return false;
    }

    if (errorMessages.accountNumber !== undefined) {
      return false;
    }

    return true;
  }

  async function onNext() {
    if (isValid()) {
      onSwitchToContactPage();
    } else {
      setForceValidation(true);
    }
  }

  return (
    <Stack gap={8}>
      <Stack gap={4}>
        <Row gap={2}>
          <YellowExclamationAnimatedIcon size={20} />
          <T size="xs" color="muted">
            Please note self-clearing is not supported.
          </T>
        </Row>

        {/* No width wrapper: the Stack lets the address grid fill the
         * dialog body, which is the width it was pinned to anyway. */}
        <AddressInput
          enabled={true}
          businessName={broker.businessName || ""}
          businessNameChanged={setBusinessName}
          locationType={LocationType.Warehouse}
          distributionWarehouseBrand={undefined}
          address={broker.address}
          addressChanged={updateAddress}
          forceValidation={forceValidation}
          businessNameErrorMessages={errorMessages.businessName}
          addressErrorMessages={errorMessages.address}
          addressOptions={[]}
          allowCityAndPostalCodeEditing={true}
          warnAboutPotentialBusinessNameErrors={false}
          accountNumber={broker.accountNumber || ""}
          accountNumberChanged={setAccountNumber}
          accountNumberErrorMessages={errorMessages.accountNumber}
          showAccountNumberInput={true}
          displayName="Customs Broker"
          customBusinessNameLabel="Customs Broker Business Name"
          maxLengths={brokersFieldLimits}
        />
      </Stack>

      <Row justify="between">
        <Button onClick={onCancel}>Cancel</Button>
        <Button primary data-testid="next-broker-button" onClick={onNext}>
          Next
        </Button>
      </Row>
    </Stack>
  );
}

export default BrokersAddBrokerDialogAddressPage;
